/**
 * FeedbackPage - Feedback page component
 * Uses AppShell for consistent layout, structured like Home2
 */

import React, { useMemo, useRef } from 'react';
import { ExternalLink } from 'lucide-react';
import AppShell from '../../layout/AppShell';
import { HeaderConfig } from '../../layout/AppHeader';
import './FeedbackPage.css';

interface FeedbackPageProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  onMyVibeClick?: () => void;
  hideNavbar?: boolean; // Hide navbar for native app
}

const FeedbackPage: React.FC<FeedbackPageProps> = ({
  activeTab,
  onTabChange,
  onMyVibeClick,
  hideNavbar = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Create header configuration - matching Home2 alignment
  const headerConfig: HeaderConfig = useMemo(() => ({
    left: <h1 className="home2-title">Feedback</h1>,
    right: undefined,
    backgroundColor: '#7c3aed',
    variant: 'default'
  }), []);

  return (
    <AppShell
      headerConfig={headerConfig}
      showFooter={!hideNavbar}
      activeTab={activeTab}
      onTabChange={onTabChange}
      onMyVibeClick={onMyVibeClick}
    >
      <div ref={containerRef} className="home2-page">
        <div className="feedback-page-content">
          <h2 className="feedback-page-title">Share Your Feedback</h2>
          <p className="feedback-intro">
            We'd love to hear your thoughts! Your feedback helps us improve VibeCheck.
          </p>
          
          <div className="feedback-section">
            <h3>📝 Complete Our Feedback Form</h3>
            <p>Help us make VibeCheck better by completing our quick survey!</p>
            <a 
              href="https://forms.gle/K7QCJ26P4TwqFpjZ9" 
              target="_blank" 
              rel="noopener noreferrer"
              className="feedback-link-button"
            >
              <span>Open Feedback Form</span>
              <ExternalLink size={16} />
            </a>
          </div>

          <div className="feedback-divider">OR</div>

          <div className="feedback-section">
            <h3>📧 Contact Us via Email</h3>
            <p>Send us an email with your thoughts, suggestions, or questions!</p>
            <a 
              href="mailto:info@vibecheck.au" 
              className="feedback-link-button email-button"
            >
              <span>info@vibecheck.au</span>
              <ExternalLink size={16} />
            </a>
          </div>

          <div className="feedback-divider">OR</div>

          <div className="feedback-section">
            <h3>💬 Message Us on Social Media</h3>
            <p>Send us a DM with your thoughts, suggestions, or just say hi!</p>
            <div className="social-links">
              <a 
                href="https://www.instagram.com/vibechecksydney" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-link instagram"
              >
                <div className="social-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </div>
                <div className="social-info">
                  <span className="social-name">Instagram</span>
                  <span className="social-handle">@vibechecksydney</span>
                </div>
              </a>

              <a 
                href="https://www.tiktok.com/@vibecheckau" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-link tiktok"
              >
                <div className="social-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </div>
                <div className="social-info">
                  <span className="social-name">TikTok</span>
                  <span className="social-handle">@vibecheckau</span>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
};

export default FeedbackPage;
